import Modal from "@/components/common/generalModal";
import { FC, useEffect, useState } from "react";
import { InputField } from "@/components/common/v2/input";
import { Button, ButtonType } from "@/components/common/button";
import { TermsAndPoliceCheckbox } from "@/components/common/termaAndPolicyCheckbox";
import { EMAIL_REGEX, EMAIL_TAKEN_ERROR_MESSAGE } from "@/constants";
import { useGoogleAuth } from "@/components/SignUpModal/hooks/useGoogleAuth";
import { useQuizContext } from "@/pages/quiz/context";
import { submitQuizAnswer } from "@/services/quizServices";
import { IdentityAnswer } from "@/pages/quiz/base/questions/calculatingYourPotentialWeightLoss";
import { GoogleButton } from "@/components/SignUpModal/GoogleButton";

interface ISignUpModalProps {
  open: boolean;
  uploadUserId?: string;
  quizId?: string;
  onClose: () => void;
  onSubmit: (values: IdentityAnswer) => void;
  version?: "v1" | "v2";
}

export const SignUpModal: FC<ISignUpModalProps> = ({
  open,
  uploadUserId,
  quizId,
  onClose,
  onSubmit,
  version = "v2",
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [fullName, setFullName] = useState({});
  const [emailReadonly, setEmailReadonly] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { initialQuestions } = useQuizContext();

  const isValidEmail = (email: string) => EMAIL_REGEX.test(email);

  const handleSubmit = async (values: IdentityAnswer) => {
    const contactFieldsQuestion = initialQuestions?.find(
      (question) => question.name === "custom-email-field"
    );

    if (contactFieldsQuestion?.id) {
      // run server validation on email duplicate
      const res = await submitQuizAnswer({
        id: contactFieldsQuestion.id,
        userId: uploadUserId ?? "",
        quizId,
        value: values,
      });

      if (res?.status === 409) {
        setError(EMAIL_TAKEN_ERROR_MESSAGE);
      } else {
        onSubmit(values);
        onClose();
      }
      return;
    }

    // if something goes wrong with questions config, skip this check
    onSubmit(values);
    onClose();
  };

  const onGoogleAuthSuccess = (values: IdentityAnswer) => {
    const email = values.email;
    setFullName({
      firstName: values.firstName,
      lastName: values.lastName,
    });
    setEmail(email);
    setEmailReadonly(true);
  };

  const { login } = useGoogleAuth(onGoogleAuthSuccess);

  const handleNormalSubmit = () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!termsAccepted) {
      setError("You must accept the terms and policy to continue.");
      return;
    }

    setError("");
    handleSubmit({ email: email.toLowerCase(), ...fullName });
  };

  useEffect(() => {
    if (!!error) setEmailReadonly(false);
  }, [error]);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <div className="max-w-[500px] w-full mx-auto">
        <h2 className="section-title text-center">
          See how much weight you could lose
        </h2>
        <div className="flex flex-col items-start justify-center gap-2">
          <InputField
            label="Email address"
            name="Email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            disabled={emailReadonly}
          />

          <TermsAndPoliceCheckbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="border-0 py-1 px-0"
            labelClassName="text-sm"
          />
          <p
            className={`text-sm w-full text-left transition-opacity duration-200 min-h-[1.25rem] ${
              error ? "text-brick-red opacity-100" : "opacity-0"
            }`}
          >
            {error || " "}
          </p>

          <Button
            onClick={handleNormalSubmit}
            version="v2"
            btnClassName={"bg-purple-3"}
            type={ButtonType.SUBMIT}
          />
          {!emailReadonly && (
            <>
              <div className="w-full flex gap-2 items-center py-2 justify-center">
                <div className="w-full h-[1px] bg-cool-gray" />
                <p className="text-second-text-v2 text-xs text-nowrap">
                  Or other log-in options
                </p>
                <div className="w-full h-[1px] bg-cool-gray " />
              </div>
              <GoogleButton version={version} onClick={login} />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
